import type { IPaymentItemOption } from "@/data/model/Order";
import { Format } from "@/data/util/Format";

import { Component } from "@/shared/elements/Component";
import { DiscountUtil } from "@/shared/utils/DiscountUtil";
import { toMoney } from "@/shared/utils/format";

import { getNameInLocationLanguage } from "@/shared/helpers/ItemNameHelper";
import { PaymentItemOptionGroup } from "@/operator/elements/PaymentItemOptionGroup";

export class PaymentItemOption extends Component {
    public readonly option: IPaymentItemOption;
    public readonly isParentItemCancelled: boolean;
    public readonly displayPrice: boolean;

    constructor(option: IPaymentItemOption, isParentItemCancelled: boolean, displayPrice: boolean = false) {
        super();
        this.option = option;
        this.isParentItemCancelled = isParentItemCancelled;
        this.displayPrice = displayPrice;

        this.$element = this.drawElement();
    }

    protected drawElement() {
        const option = this.option;

        const $element = $('<div class="item" />');
        const $optionHead = $('<div class="head" />').appendTo($element);
        const $optionName = $('<div class="name" />').appendTo($optionHead);

        if (option.quantity !== 1) $optionName.append(`<div class="quantity">${option.quantity}x</div>`);

        $optionName.append(this.getDisplayName() ?? "");

        if (this.displayPrice) {
            if (this.option.hasDiscount && !this.option.isCancelled && !this.option.isZeroPriceArticle) {
                const icon = DiscountUtil.getDiscountIcon(this.option.lastDiscountType);

                $optionHead.append(`<div class="price has-discount">
                    <div class="price-before-discount" style="color: #fd397a; text-decoration: line-through;">
                        <span class="discount-type">
                            <i class="${icon}"></i>
                        </span>
                        ${this.getDisplayPrice()}
                    </div>
                    <div class="price-after-discount">
                        ${toMoney(this.option.currentTotalAmountAfterDiscount, true)}
                    </div>
                </div>`);
            } else {
                $optionHead.append(`<div class="price">${this.getDisplayPrice()}</div>`);
            }
        }

        // draw option groups
        if (option.options.length) {
            const $subContainer = $('<div class="subs" />').appendTo($element);
            for (const optionGroup of option.options) {
                PaymentItemOptionGroup.render(
                    $subContainer,
                    optionGroup,
                    this.isParentItemCancelled,
                    this.displayPrice
                );
            }
        }

        return $element;
    }

    private getDisplayName(): string | undefined {
        return Format.htmlEscape(getNameInLocationLanguage(this.option, true));
    }

    private getDisplayPrice() {
        if (this.option.isZeroPriceArticle) return "";

        return toMoney(this.option.isCancelled ? this.option.totalAmount : this.option.currentTotalAmount, true);
    }

    public static render(
        $appendToTarget: JQuery,
        option: IPaymentItemOption,
        isParentItemCancelled: boolean,
        displayPrice: boolean = false
    ): PaymentItemOption {
        const component = new PaymentItemOption(option, isParentItemCancelled, displayPrice);
        component.renderTo($appendToTarget);

        return component;
    }
}
